// Tiny API client — attaches bearer token, parses JSON, surfaces nice errors.
// Supports per-call extra headers via the `headers` option (used by the
// admin API client to attach `X-Workspace`).
const BASE = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'rb-token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// ── Data-change event bus (Phase 17) ─────────────────────────────────────
// Mutations to cards / transactions / Gmail drafts emit a `rb:data-changed`
// CustomEvent on the window. Pages that show derived data (Leakage, Dashboard
// KPIs, Milestones) listen and refetch. Keeps cross-page state consistent
// without forcing a page reload after every edit.
//
// Event detail: { entity: 'card' | 'transaction' | 'gmail', action: 'create'|'update'|'delete' }
export const dispatchDataChange = (entity, action) => {
  try {
    window.dispatchEvent(new CustomEvent('rb:data-changed', {
      detail: { entity, action, at: Date.now() },
    }));
  } catch (_) { /* SSR / non-browser env */ }
};

async function request(method, path, body, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (!res.ok) {
    const message = (data && (data.detail || data.message)) || `${method} ${path} failed`;
    const err = new Error(typeof message === 'string' ? message : 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Multipart POST — used for file uploads (PDF ingestion).
// Do NOT set Content-Type explicitly; the browser must set it including the
// `boundary=` token, otherwise the server can't parse the form.
async function requestForm(path, formData, options = {}) {
  const headers = { ...(options.headers || {}) };
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (!res.ok) {
    const message = (data && (data.detail || data.message)) || `POST ${path} failed`;
    const err = new Error(typeof message === 'string' ? message : 'Upload failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  get: (path, options) => request('GET', path, undefined, options),
  post: (path, body, options) => request('POST', path, body, options),
  patch: (path, body, options) => request('PATCH', path, body, options),
  del: (path, options) => request('DELETE', path, undefined, options),
  postForm: (path, formData, options) => requestForm(path, formData, options),
};

// Endpoints
export const Auth = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  register: (name, email, password) => api.post('/api/auth/register', { name, email, password }),
  demo: () => api.post('/api/auth/demo-login'),
  me: () => api.get('/api/auth/me'),
  patchMe: (patch) => api.patch('/api/auth/me', patch),
  googleLoginUrl: (redirect) =>
    api.get(`/api/auth/google/login-url?redirect=${encodeURIComponent(redirect)}`),
  googleSession: async (sessionId) => {
    // Custom request because we need the X-Session-ID header. We deliberately
    // don't include the existing JWT (this call ESTABLISHES a session).
    const res = await fetch(`${BASE}/api/auth/google/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId,
      },
      body: JSON.stringify({ session_id: sessionId }),
    });
    let data = null;
    try { data = await res.json(); } catch (_) {}
    if (!res.ok) {
      const message = (data && (data.detail || data.message)) || 'Google sign-in failed';
      const err = new Error(typeof message === 'string' ? message : 'Auth failed');
      err.status = res.status;
      throw err;
    }
    return data;
  },
  migrateDemo: () => api.post('/api/auth/migrate-demo', { source: 'demo' }),
  // Phase 10.4 — native Google OAuth (replaces the Emergent proxy hop so
  // the consent screen renders "CardAssist Online" from Google Cloud
  // Console rather than the subdomain-derived "Rewards Brain").
  googleNativeLoginUrl: (redirect) =>
    api.get(`/api/auth/google-native/login-url${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`),
};

export const Cards = {
  library: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/api/cards/library${q ? `?${q}` : ''}`);
  },
  detail: (id) => api.get(`/api/cards/library/${id}`),
  banks: () => api.get('/api/cards/banks'),
  mine: () => api.get('/api/cards/me'),
  add: async (payload) => {
    const r = await api.post('/api/cards/me', payload);
    dispatchDataChange('card', 'create');
    return r;
  },
  update: async (cardId, payload) => {
    const r = await api.patch(`/api/cards/me/${cardId}`, payload);
    dispatchDataChange('card', 'update');
    return r;
  },
  remove: async (cardId) => {
    const r = await api.del(`/api/cards/me/${cardId}`);
    dispatchDataChange('card', 'delete');
    return r;
  },
};

export const Recommend = {
  run: (payload) => api.post('/api/recommend', payload),
  parseIntent: (text) => api.post('/api/recommend/parse-intent', { text }),
  logs: () => api.get('/api/recommend/logs'),
};

export const Transactions = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/api/transactions${q ? `?${q}` : ''}`);
  },
  create: async (payload) => {
    const r = await api.post('/api/transactions', payload);
    dispatchDataChange('transaction', 'create');
    return r;
  },
  update: async (id, payload) => {
    const r = await api.patch(`/api/transactions/${id}`, payload);
    dispatchDataChange('transaction', 'update');
    return r;
  },
  remove: async (id) => {
    const r = await api.del(`/api/transactions/${id}`);
    dispatchDataChange('transaction', 'delete');
    return r;
  },
  delete: async (id) => {
    const r = await api.del(`/api/transactions/${id}`);
    dispatchDataChange('transaction', 'delete');
    return r;
  },
  summary: () => api.get('/api/transactions/summary'),
};

export const Offers = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/api/offers${q ? `?${q}` : ''}`);
  },
};

export const Milestones = { mine: () => api.get('/api/milestones') };
export const Merchants = {
  list: () => api.get('/api/merchants'),
  detail: (slug) => api.get(`/api/merchants/${slug}`),
};
export const Leakage = { mine: () => api.get('/api/leakage') };
export const MCC = {
  search: (q, limit = 25) => api.get(`/api/mcc?q=${encodeURIComponent(q || '')}&limit=${limit}`),
  find: (code) => api.get(`/api/mcc/${code}`),
};
export const Assistant = {
  chat: (sessionId, message) => api.post('/api/assistant/chat', { session_id: sessionId, message }),
};

// Gmail Statement Sync (Phase 8) — connect Gmail, parse statement emails,
// review parsed drafts before saving them as real transactions.
export const Gmail = {
  status: () => api.get('/api/gmail/status'),
  loginUrl: () => api.get('/api/oauth/gmail/login'),
  sync: (payload = {}) => api.post('/api/gmail/sync', {
    lookback_days: payload.lookback_days ?? 60,
    // Phase 18 — match the backend's new default of 100. Earlier UI cap
    // of 30 was silently truncating "last 60 days" to the most recent few
    // days for heavy spenders.
    max_messages: payload.max_messages ?? 100,
  }),
  drafts: () => api.get('/api/gmail/drafts'),
  apply: async (items) => {
    const r = await api.post('/api/gmail/apply', { items });
    // Applying drafts INSERTS real transactions — same as a manual create
    // from the user's perspective. Notify dependents (Leakage, Dashboard).
    dispatchDataChange('transaction', 'create');
    return r;
  },
  discard: (draft_id) => api.post('/api/gmail/discard', { draft_id }),
  // Phase 20 — bulk-dismiss every pending draft (server-side).
  discardAll: () => api.post('/api/gmail/discard-all', {}),
  disconnect: () => api.post('/api/gmail/disconnect'),
  bannerStatus: () => api.get('/api/gmail/auto-imports/banner'),
  bannerDismiss: () => api.post('/api/gmail/auto-imports/banner/dismiss'),
  triggerBackgroundSync: () => api.post('/api/gmail/background-sync/trigger'),
};
